/**
 * Documents tab body — upload form (left) + table of uploaded documents (right).
 */
import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CloudArrowUp, DownloadSimple, Trash } from "@phosphor-icons/react";

export const DOC_TYPE_LABELS = {
  birth_cert: "Birth certificate",
  address_proof: "Address proof",
  parent_photo: "Parent photo",
  previous_records: "Previous records",
  other: "Other",
};

export function DocumentsTab({ docs, docForm, setDocForm, onDocUpload, onDeleteDoc, downloadDoc, canEdit }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
      <div className="lg:col-span-1 vs-card p-5 space-y-3">
        <div className="eyebrow">Upload document</div>
        <div>
          <Label>Type</Label>
          <Select value={docForm.doc_type} onValueChange={(v) => setDocForm({ ...docForm, doc_type: v })}>
            <SelectTrigger data-testid="doc-type-select" className="bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(DOC_TYPE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Description</Label>
          <Input value={docForm.description} onChange={(e) => setDocForm({ ...docForm, description: e.target.value })} className="bg-white" placeholder="Optional notes" />
        </div>
        <label className="flex items-center justify-center gap-2 px-4 py-3 rounded-md border-2 border-dashed border-[var(--vs-border-focus)]/40 hover:border-[var(--vs-secondary)] cursor-pointer text-sm transition-colors">
          <CloudArrowUp size={16} weight="duotone" /> Choose file (PDF/JPG/PNG · max 5MB)
          <input
            data-testid="doc-file-input"
            type="file"
            accept="application/pdf,image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => onDocUpload(e.target.files?.[0])}
          />
        </label>
      </div>

      <div className="lg:col-span-2 vs-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--vs-bg-subtle)]">
            <tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]">
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">File</th>
              <th className="px-4 py-3">Size</th>
              <th className="px-4 py-3">When</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {docs.map((d) => (
              <tr key={d.id} data-testid={`doc-row-${d.id}`} className="border-t border-[var(--vs-border)]">
                <td className="px-4 py-3 capitalize">{DOC_TYPE_LABELS[d.doc_type] || d.doc_type}</td>
                <td className="px-4 py-3 font-mono text-xs">{d.filename || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs">{d.size ? `${Math.round(d.size / 1024)}KB` : "—"}</td>
                <td className="px-4 py-3 font-mono text-xs">{new Date(d.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 flex items-center gap-2">
                  <Button size="sm" variant="outline" data-testid={`download-doc-${d.id}`} onClick={() => downloadDoc(d)}>
                    <DownloadSimple size={12} className="mr-1.5" /> View
                  </Button>
                  {canEdit && (
                    <Button size="sm" variant="ghost" data-testid={`delete-doc-${d.id}`} onClick={() => onDeleteDoc(d.id)}>
                      <Trash size={12} className="text-[var(--vs-danger)]" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {docs.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-[var(--vs-text-secondary)]">No documents uploaded yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
